import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  useDeviceType,
  useDialogHandler,
  DESKTOP_SCREEN,
  MOBILE_SCREEN,
} from '@travel/traveler-core/hooks';
import { toSEOSearchString } from '@travel/traveler-core/utils/seo';

import DataLayer from 'components/DataLayer';
import Dialog from 'components/Dialog';
import {
  InternalSalesPromotionFormQuery,
  SalesPromotionFormQuery,
} from 'components/SalesPromotionInput';
import { RAT_PAGE_TYPE_OBJ } from 'constants/ratAnalytics';
import { OFFER_TYPE } from 'constants/salesPromotion';
import paths from 'core/universalRouter/paths';
import { RoomFormQuery } from 'pages/ProviderListPage/components/SearchForm';
import {
  getNoOfAppliedCoupons,
  getNoOfAvailableCoupons,
  getPlanDetail,
} from 'store/providerRatePlan/selectors';
import PlanCouponItem from '../PlanCouponItem';
import ReserveButton from '../ReserveButton';
import RoomPlanItemCard from '../RoomPlanItemCard';
import PlanDetails from './PlanDetails';

import { pushLocation } from 'store/__router/actions';

import { Translate } from 'core/translate';
import { Media } from 'ProviderInformation-Types';
import { SearchFormQuery } from 'ProviderList-Types';
import { Plan } from 'ProviderRatePlan-Types';

import { getRouter } from 'store/__router/selectors';
import { getLimitationsForCurrentMarket } from 'store/markets/selectors';
import { getMember } from 'store/member/selectors';

import styles from './roomList.module.scss';

export type Props = {
  /** on what parent page this component lives */
  onPage: 'providerInfoUndated' | 'providerInfoDated';
  /** Display image */
  displayImage?: Media;
  /** Provider id */
  providerId: string;
  /** Plan id */
  planId: string;
  /** Rate plan id */
  ratePlanId: string;
  /** Item plan id */
  itemId: string;
  /** SalesPromotion id */
  appliedSalesPromotionIds?: string[] | null;
  /** Price before discount */
  perRoomPriceBeforeDiscount: number;
  /** Price after discount */
  perRoomPriceAfterDiscount: number | null;
  /** Current search params */
  searchParam: RoomFormQuery & SalesPromotionFormQuery & InternalSalesPromotionFormQuery;
  /** Callback to be called when date is selected */
  onSelectDate: (startDate?: string, endDate?: string) => void;
  /** Search query */
  query: SearchFormQuery;
};

function PlanItem(props: Props) {
  const { searchParam, onSelectDate, onPage } = props;
  const { isMember } = useSelector(getMember);
  const {
    isOpen: isOpenPlanDetail,
    onClose: onClosePlanDetail,
    onOpen: onDisplayPlanDetail,
  } = useDialogHandler(false);

  const dispatch = useDispatch();
  const deviceType = useDeviceType();
  const isMobile = deviceType === MOBILE_SCREEN;
  const isDesktop = deviceType === DESKTOP_SCREEN;
  const { baseUrl } = useSelector(getRouter);
  const limitations = useSelector(getLimitationsForCurrentMarket);

  const plan: Plan = useSelector(getPlanDetail(props.itemId, props.planId));

  const noOfAppliedCoupons: number = useSelector(getNoOfAppliedCoupons(props.itemId, props.planId));
  const noOfAvailableCoupons: number = useSelector(
    getNoOfAvailableCoupons(props.itemId, props.planId),
  );

  const { pageType, siteSection, parentName } = RAT_PAGE_TYPE_OBJ[onPage];

  const isCoupon = plan?.price?.salesPromotions?.some(sp => sp.offerType === OFFER_TYPE.COUPON);

  // reserve button
  const onClickReserveButton = () => {
    const searchString = toSEOSearchString(
      {
        itemId: props.itemId,
        ratePlanId: props.planId,
        itemRatePlanId: props.ratePlanId,
        startDate: searchParam.startDate,
        endDate: searchParam.endDate,
        noOfUnits: searchParam.noOfUnits,
        adults: searchParam.adults,
        childrenAges: searchParam.childrenAges,
        promotionCode: searchParam.promotionCode,
        appliedSalesPromotionIds: props.appliedSalesPromotionIds?.join(','),
        perRoomPriceBeforeDiscount: String(props.perRoomPriceBeforeDiscount),
        perRoomPriceAfterDiscount: String(props.perRoomPriceAfterDiscount),
        isCouponSpecified: searchParam.isCouponSpecified,
      },
      limitations,
    );

    const bookingLocation = `${paths.bookingStep1.pathResolver(props.providerId)}?${searchString}`;

    if (isMember) {
      dispatch(pushLocation(bookingLocation));
    } else {
      dispatch(
        pushLocation(
          `${paths.loginOrGuest.path}?callback=${encodeURIComponent(
            `${baseUrl}${bookingLocation}`,
          )}`,
        ),
      );
    }
  };

  // coupon element
  const couponBadge = (
    <PlanCouponItem
      itemId={props.itemId}
      ratePlanId={props.ratePlanId}
      planId={props.planId}
      className={styles.card}
      combinedIncentive={plan?.price.combinedIncentive}
      salesPromotions={plan?.price.salesPromotions}
      priceAfterDiscount={plan?.price.total}
      noOfAppliedCoupons={noOfAppliedCoupons}
      noOfAvailableCoupons={noOfAvailableCoupons}
      query={props.query}
      pageIdentifier={props.providerId}
      parentComponent={'ItemCard'}
    />
  );

  return (
    <>
      <RoomPlanItemCard
        itemType="plan"
        availabilityCount={plan?.availabilityCount}
        cancellationPenalties={plan?.cancellationPenalties}
        hasMeal={plan?.hasMeal}
        facilities={plan?.facilities}
        media={plan?.media}
        paymentRestrictionFeature={plan?.paymentRestrictionFeature}
        itemTitle={plan?.name}
        price={plan?.price}
        points={plan?.points}
        searchParam={props.searchParam}
        couponBadge={isCoupon && couponBadge}
        onDisplayDetailDialog={onDisplayPlanDetail}
        onReserve={onClickReserveButton}
        onSelectDate={onSelectDate}
        itemRatePlanId={plan.itemRatePlanId}
      />

      <Dialog
        title={isDesktop ? null : <Translate id="Provider_Info.Room_List.Plan_Details.Title" />}
        isOpen={isOpenPlanDetail}
        isSEOContentIndexedOnHidden={true}
        onClose={onClosePlanDetail}
        hasEnableBack={false}
        isUseContentFlexBox={false}
        className={styles.withActionDialog}
        contentClassName={styles.dialogContent}
      >
        <div>
          {isDesktop && (
            <h5 className={styles.dialogTitle}>
              <Translate id="Provider_Info.Room_List.Plan_Details.Title" />
            </h5>
          )}
          <PlanDetails
            onPage={onPage}
            itemId={props.itemId}
            planId={props.planId}
            ratePlanId={props.ratePlanId}
            appliedSalesPromotionIds={props.appliedSalesPromotionIds}
            perRoomPriceBeforeDiscount={props.perRoomPriceBeforeDiscount}
            perRoomPriceAfterDiscount={props.perRoomPriceAfterDiscount}
            providerId={props.providerId}
            searchParam={props.searchParam}
            displayImage={props.displayImage}
            onClose={onClosePlanDetail}
            onSelectDate={onSelectDate}
            query={props.query}
            onReserve={onClickReserveButton}
          />
          {/* Cannot use a built-in button on dialog due to custom event */}
          {isMobile && (
            <div className={styles.actionBox}>
              <ReserveButton
                searchParam={searchParam}
                onClick={onClickReserveButton}
                onSelectDate={onSelectDate}
                shouldShowNoAccountNeeded={false}
              />
            </div>
          )}
        </div>
      </Dialog>

      {isOpenPlanDetail && (
        <DataLayer
          pageName={`${parentName}:plan_details_popup`}
          pageType={pageType}
          siteSection={siteSection}
        />
      )}
    </>
  );
}

export default React.memo(PlanItem);
